import React, { useState, useEffect } from 'react';
import {
  api,
  IntegrationMonitor,
  IntegrationRun,
  DiscoveredIntegration,
  DeploymentRegressionAlert,
  CreateIntegrationMonitorInput,
  IntegrationTemplate,
} from '../api';
import type { IntegrationDirection, IntegrationEntity, IntegrationExecutionType } from '../api';
import { humanize } from '../labels';
import { IntegrationWatchersDrawer } from './IntegrationWatchersDrawer';
import { ActionMenu, Callout, Dialog, Input, RowActions, Section, Select, Subsection, Table } from './primitives';
import { SshStat, SshStats } from './SshStats';
import { ConfirmDialog, Field, LoadingRows, StatusBadge } from './ui';

/** How an integration runs, in the words of the form and the list. */
const executionTypeLabels: Record<IntegrationExecutionType, string> = {
  cron: 'Cron job',
  queue_consumer: 'Queue consumer',
  method_plugin: 'PHP method plugin',
  magento_event: 'Magento event',
  http_api: 'HTTP API',
};

const directionLabels: Record<IntegrationDirection, string> = {
  export: 'Export',
  import: 'Import',
  sync: 'Two-way sync',
};

/** The verb of the one-line summary: "exports orders". */
const directionVerbs: Record<IntegrationDirection, string> = {
  export: 'exports',
  import: 'imports',
  sync: 'syncs',
};

const entityLabels: Record<IntegrationEntity, string> = {
  orders: 'Orders',
  products: 'Products',
  stock: 'Stock',
  prices: 'Prices',
  customers: 'Customers',
  shipments: 'Shipments',
  custom: 'Custom data',
};

function labelOf<T extends string>(labels: Record<T, string>, value: T): string {
  return labels[value] ?? humanize(value);
}

/** "Cron job acme_export exports orders, expected every 5m." */
function integrationSummary(
  item: { execution_type: IntegrationExecutionType; target_identifier: string; direction: IntegrationDirection; entity: IntegrationEntity },
  interval?: string,
) {
  return (
    <>
      {labelOf(executionTypeLabels, item.execution_type)} <code>{item.target_identifier}</code>{' '}
      {directionVerbs[item.direction] ?? humanize(item.direction).toLowerCase()} {labelOf(entityLabels, item.entity).toLowerCase()}
      {interval ? `, expected every ${interval}` : ''}.
    </>
  );
}

interface Props {
  environmentId: string;
}

export const MagentoIntegrationMonitorCard: React.FC<Props> = ({ environmentId }) => {
  const [monitors, setMonitors] = useState<IntegrationMonitor[]>([]);
  const [discovered, setDiscovered] = useState<DiscoveredIntegration[]>([]);
  const [regressions, setRegressions] = useState<DeploymentRegressionAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [showDiscovered, setShowDiscovered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Runs modal / drawer state
  const [selectedMonitor, setSelectedMonitor] = useState<IntegrationMonitor | null>(null);
  const [runs, setRuns] = useState<IntegrationRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  // Watchers drawer state
  const [selectedWatchersMonitor, setSelectedWatchersMonitor] = useState<IntegrationMonitor | null>(null);

  // Template modal state
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templates, setTemplates] = useState<IntegrationTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [instantiatingTemplate, setInstantiatingTemplate] = useState<string | null>(null);

  // Quick evaluation state per monitor
  const [evaluatingMonitorId, setEvaluatingMonitorId] = useState<string | null>(null);

  // Create / Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState<IntegrationMonitor | null>(null);
  const [formData, setFormData] = useState<CreateIntegrationMonitorInput>({
    name: '',
    execution_type: 'cron',
    target_identifier: '',
    direction: 'export',
    entity: 'orders',
    expected_interval_sec: 300,
    warn_after_sec: 600,
    critical_after_sec: 1800,
  });
  const [savingMonitor, setSavingMonitor] = useState(false);
  const [restartingConsumer, setRestartingConsumer] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [monList, regList] = await Promise.all([
        api.listIntegrationMonitors(environmentId),
        api.getDeploymentRegressions(environmentId),
      ]);
      setMonitors(monList);
      setRegressions(regList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load integration monitors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [environmentId]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const updated = await api.syncIntegrationStatuses(environmentId);
      setMonitors(updated);
      const regList = await api.getDeploymentRegressions(environmentId);
      setRegressions(regList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync integration statuses');
    } finally {
      setSyncing(false);
    }
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    setError(null);
    try {
      const list = await api.discoverIntegrations(environmentId);
      setDiscovered(list);
      setShowDiscovered(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discover integrations');
    } finally {
      setDiscovering(false);
    }
  };

  const handleQuickAdd = async (item: DiscoveredIntegration) => {
    setError(null);
    try {
      await api.createIntegrationMonitor(environmentId, {
        name: item.name,
        execution_type: item.execution_type,
        target_identifier: item.target_identifier,
        direction: item.direction,
        entity: item.entity,
        expected_interval_sec: item.suggested_interval_sec,
        warn_after_sec: item.suggested_interval_sec * 2,
        critical_after_sec: item.suggested_interval_sec * 4,
      });
      // Refresh
      const [monList, discList] = await Promise.all([
        api.listIntegrationMonitors(environmentId),
        api.discoverIntegrations(environmentId),
      ]);
      setMonitors(monList);
      setDiscovered(discList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add integration monitor');
    }
  };

  const handleOpenRuns = async (m: IntegrationMonitor) => {
    setSelectedMonitor(m);
    setLoadingRuns(true);
    try {
      const res = await api.listIntegrationRuns(environmentId, m.id, 25);
      setRuns(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch runs');
    } finally {
      setLoadingRuns(false);
    }
  };

  const handleRestartConsumer = async (consumerName: string) => {
    setRestartingConsumer(consumerName);
    setError(null);
    try {
      await api.restartQueueConsumer(environmentId, consumerName);
      await handleSync();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restart queue consumer');
    } finally {
      setRestartingConsumer(null);
    }
  };

  const handleOpenTemplates = async () => {
    setIsTemplateModalOpen(true);
    setLoadingTemplates(true);
    try {
      const tpls = await api.listIntegrationTemplates(environmentId);
      setTemplates(tpls);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    setInstantiatingTemplate(templateId);
    setError(null);
    try {
      const created = await api.createMonitorFromTemplate(environmentId, templateId);
      setIsTemplateModalOpen(false);
      await loadData();
      setSelectedWatchersMonitor(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create monitor from template');
    } finally {
      setInstantiatingTemplate(null);
    }
  };

  const handleEvaluateMonitor = async (monitorId: string) => {
    setEvaluatingMonitorId(monitorId);
    setError(null);
    try {
      const { monitor: updated } = await api.evaluateIntegrationMonitor(environmentId, monitorId);
      setMonitors((prev) => prev.map((m) => (m.id === monitorId ? updated : m)));
      if (selectedWatchersMonitor && selectedWatchersMonitor.id === monitorId) {
        setSelectedWatchersMonitor(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to evaluate monitor signals');
    } finally {
      setEvaluatingMonitorId(null);
    }
  };

  const [monitorToDelete, setMonitorToDelete] = useState<string | null>(null);
  const [deletingMonitor, setDeletingMonitor] = useState(false);
  const handleDelete = (monitorId: string) => setMonitorToDelete(monitorId);
  const confirmDelete = async () => {
    const monitorId = monitorToDelete;
    if (!monitorId) return;
    setDeletingMonitor(true);
    try {
      await api.deleteIntegrationMonitor(environmentId, monitorId);
      setMonitors(monitors.filter((m) => m.id !== monitorId));
      setMonitorToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete monitor');
    } finally {
      setDeletingMonitor(false);
    }
  };

  const handleSaveModal = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingMonitor(true);
    setError(null);
    try {
      if (editingMonitor) {
        await api.updateIntegrationMonitor(environmentId, editingMonitor.id, formData);
      } else {
        await api.createIntegrationMonitor(environmentId, formData);
      }
      setIsModalOpen(false);
      setEditingMonitor(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save monitor');
    } finally {
      setSavingMonitor(false);
    }
  };

  const openCreateModal = () => {
    setEditingMonitor(null);
    setFormData({
      name: '',
      execution_type: 'cron',
      target_identifier: '',
      direction: 'export',
      entity: 'orders',
      expected_interval_sec: 300,
      warn_after_sec: 600,
      critical_after_sec: 1800,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (m: IntegrationMonitor) => {
    setEditingMonitor(m);
    setFormData({
      name: m.name,
      execution_type: m.execution_type,
      target_identifier: m.target_identifier,
      direction: m.direction,
      entity: m.entity,
      expected_interval_sec: m.expected_interval_sec,
      warn_after_sec: m.warn_after_sec,
      critical_after_sec: m.critical_after_sec,
    });
    setIsModalOpen(true);
  };

  const formatSeconds = (sec: number) => {
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.round(sec / 60)}m`;
    return `${Math.round(sec / 3600)}h`;
  };

  const formatTimeAgo = (iso?: string) => {
    if (!iso) return 'Never';
    const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const healthyCount = monitors.filter((m) => m.status === 'healthy').length;
  const delayedCount = monitors.filter((m) => m.status === 'delayed' || m.status === 'stale').length;
  const failingCount = monitors.filter((m) => m.status === 'failing').length;

  return (
    <Section
      title="Integration monitoring"
      testId="integration-monitors"
      actions={
        <>
          <button type="button" onClick={handleSync} disabled={syncing} className="button button--secondary">
            {syncing ? 'Syncing' : 'Sync statuses'}
          </button>
          <button type="button" onClick={handleDiscover} disabled={discovering} className="button button--secondary">
            {discovering ? 'Discovering' : 'Discover integrations'}
          </button>
          <button type="button" onClick={handleOpenTemplates} className="button button--secondary">
            Add from a template
          </button>
          <button type="button" onClick={openCreateModal} className="button">
            Add monitor
          </button>
        </>
      }
    >
      <ConfirmDialog
        open={monitorToDelete !== null}
        title={`Remove the integration monitor “${monitors.find((m) => m.id === monitorToDelete)?.name ?? ''}”?`}
        description="Commerce Ops stops watching this integration; its run history and technical signal watchers are removed with it. The integration itself in Magento is not touched."
        confirmLabel="Remove monitor"
        danger
        busy={deletingMonitor}
        onConfirm={confirmDelete}
        onCancel={() => setMonitorToDelete(null)}
      />

      {error ? (
        <Callout tone="danger" title="That did not work">
          {error}
        </Callout>
      ) : null}

      {regressions.length > 0 ? (
        <Callout tone="warning" title="A deployment may have broken an integration">
          <ul>
            {regressions.map((reg) => (
              <li key={reg.monitor_id}>
                <strong>{reg.monitor_name}</strong> began failing about{' '}
                <span className="tabular-nums">{Math.round(reg.time_delta_seconds / 60)}</span> minutes after deployment{' '}
                <code>{reg.deployment_id.slice(0, 8)}</code>.
                {reg.error_message ? ` Error: “${reg.error_message}”.` : ''}
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      <SshStats testId="integration-monitor-counts">
        <SshStat label="Monitored" value={monitors.length} />
        <SshStat label="Healthy" value={healthyCount} />
        <SshStat label="Delayed or stale" value={delayedCount} />
        <SshStat label="Failing" value={failingCount} />
      </SshStats>

      {showDiscovered ? (
        <Subsection
          title={`Discovered integrations (${discovered.length})`}
          actions={
            <button type="button" onClick={() => setShowDiscovered(false)} className="button button--ghost button--small">
              Dismiss
            </button>
          }
        >
          {discovered.length === 0 ? (
            <p className="quiet-empty">No unmonitored custom integrations found.</p>
          ) : (
            <ul className="status-list status-list--stacked" aria-label="Discovered integrations">
              {discovered.map((item) => (
                <li key={`${item.execution_type}:${item.target_identifier}`} className="status-list__item">
                  <span className="status-list__name">
                    <span>{item.name}</span> <span className="ssh-meta">{item.module}</span>
                  </span>
                  {item.monitored ? (
                    <StatusBadge kind="toggle" value="enabled" label="Monitored" />
                  ) : (
                    <button type="button" onClick={() => handleQuickAdd(item)} className="button button--secondary button--small">
                      Start monitoring
                    </button>
                  )}
                  <div className="status-list__detail">{integrationSummary(item)}</div>
                </li>
              ))}
            </ul>
          )}
        </Subsection>
      ) : null}

      <Subsection title={loading ? 'Monitors' : `Monitors (${monitors.length})`}>
        {loading ? (
          <LoadingRows label="Loading integration monitors…" rows={3} rowHeight="56px" />
        ) : monitors.length === 0 ? (
          <p className="quiet-empty">
            No monitors ·{' '}
            <button type="button" onClick={handleDiscover} className="link-button">
              Discover integrations
            </button>
          </p>
        ) : (
          <ul className="ssh-rows" aria-label="Integration monitors">
            {monitors.map((m) => (
              <li key={m.id} className="ssh-rows__item">
                <div className="ssh-rows__head">
                  <div className="ssh-rows__title">
                    <span className="ssh-rows__name">{m.name}</span>
                    <StatusBadge kind="integrationMonitor" value={m.status} />
                  </div>
                  <RowActions>
                    <button type="button" onClick={() => setSelectedWatchersMonitor(m)} className="button button--secondary button--small">
                      Inspect signals
                    </button>
                    <button type="button" onClick={() => handleOpenRuns(m)} className="button button--secondary button--small">
                      View runs
                    </button>
                    <ActionMenu
                      label={`More actions for ${m.name}`}
                      size="sm"
                      items={[
                        {
                          label: evaluatingMonitorId === m.id ? 'Probing' : 'Probe signals now',
                          onClick: () => handleEvaluateMonitor(m.id),
                          disabled: evaluatingMonitorId === m.id,
                        },
                        ...(m.execution_type === 'queue_consumer'
                          ? [
                              {
                                label: restartingConsumer === m.target_identifier ? 'Restarting' : 'Restart consumer',
                                onClick: () => handleRestartConsumer(m.target_identifier),
                                disabled: restartingConsumer === m.target_identifier,
                              },
                            ]
                          : []),
                        { label: 'Edit', onClick: () => openEditModal(m) },
                        { label: 'Remove', onClick: () => handleDelete(m.id), danger: true },
                      ]}
                    />
                  </RowActions>
                </div>

                <p className="ssh-rows__text">{integrationSummary(m, formatSeconds(m.expected_interval_sec))}</p>

                {m.composite_explanation ? (
                  <Callout
                    tone={m.status === 'failing' ? 'danger' : m.status === 'delayed' || m.status === 'stale' ? 'warning' : m.status === 'healthy' ? 'success' : 'info'}
                    role="status"
                    title="Health narrative"
                    actions={
                      <button type="button" onClick={() => setSelectedWatchersMonitor(m)} className="button button--secondary button--small">
                        View details
                      </button>
                    }
                  >
                    {m.composite_explanation}
                  </Callout>
                ) : null}

                <SshStats>
                  <SshStat label="Last run" value={formatTimeAgo(m.last_run_at)} />
                  <SshStat label="Last batch" value={`${m.last_batch_size} records`} />
                  <SshStat label="Duration" value={m.last_duration_ms > 0 ? `${(m.last_duration_ms / 1000).toFixed(1)} s` : '0 s'} />
                  <SshStat
                    label="Success in 24 h"
                    value={
                      <StatusBadge
                        kind="level"
                        value={m.success_rate_24h >= 95 ? 'ok' : m.success_rate_24h >= 80 ? 'warning' : 'critical'}
                        label={`${m.success_rate_24h.toFixed(1)}%`}
                        className="tabular-nums"
                      />
                    }
                  />
                </SshStats>
              </li>
            ))}
          </ul>
        )}
      </Subsection>

      <Dialog
        open={selectedMonitor !== null}
        size="lg"
        title={`Runs of ${selectedMonitor?.name ?? ''}`}
        description={<span className="font-mono">{selectedMonitor?.target_identifier}</span>}
        onClose={() => setSelectedMonitor(null)}
      >
        {loadingRuns ? (
          <LoadingRows label="Loading runs…" rows={4} rowHeight="32px" />
        ) : runs.length === 0 ? (
          <p className="quiet-empty">No runs recorded for this monitor yet.</p>
        ) : (
          <Table label="Runs" compact>
            <thead>
              <tr>
                <th scope="col">Started</th>
                <th scope="col">Status</th>
                <th scope="col">Duration</th>
                <th scope="col">Processed</th>
                <th scope="col">Error or note</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <th scope="row" className="tabular-nums">
                    {new Date(r.started_at).toLocaleTimeString()}
                  </th>
                  <td>
                    <StatusBadge kind="integrationMonitor" value={r.status} />
                  </td>
                  <td className="tabular-nums">{(r.duration_ms / 1000).toFixed(1)} s</td>
                  <td className="tabular-nums">
                    {r.records_processed} / {r.records_total || r.records_processed}
                  </td>
                  <td className={r.error_message ? 'ssh-advice' : 'muted'}>{r.error_message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Dialog>

      <Dialog
        open={isModalOpen}
        title={editingMonitor ? 'Edit integration monitor' : 'Add an integration monitor'}
        description="Commerce Ops expects a run within the interval and raises a warning, then a critical alert, when none arrives."
        onClose={() => setIsModalOpen(false)}
        busy={savingMonitor}
        onSubmit={handleSaveModal}
        footer={
          <>
            <button type="button" onClick={() => setIsModalOpen(false)} className="button button--secondary" disabled={savingMonitor}>
              Cancel
            </button>
            <button type="submit" disabled={savingMonitor} className="button">
              {savingMonitor ? 'Saving' : editingMonitor ? 'Save changes' : 'Create monitor'}
            </button>
          </>
        }
      >
        <Field label="Name">
          <Input
            id="monitor-name"
            type="text"
            required
            placeholder="e.g. SAP Order Export"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </Field>
        <div className="form-grid form-grid--2">
          <Field label="Runs as">
            <Select
              id="monitor-execution-type"
              value={formData.execution_type}
              onChange={(e) => setFormData({ ...formData, execution_type: e.target.value as IntegrationExecutionType })}
            >
              {(Object.keys(executionTypeLabels) as IntegrationExecutionType[]).map((value) => (
                <option key={value} value={value}>
                  {executionTypeLabels[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Direction">
            <Select
              id="monitor-direction"
              value={formData.direction}
              onChange={(e) => setFormData({ ...formData, direction: e.target.value as IntegrationDirection })}
            >
              {(Object.keys(directionLabels) as IntegrationDirection[]).map((value) => (
                <option key={value} value={value}>
                  {directionLabels[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Data">
            <Select
              id="monitor-entity"
              value={formData.entity}
              onChange={(e) => setFormData({ ...formData, entity: e.target.value as IntegrationEntity })}
            >
              {(Object.keys(entityLabels) as IntegrationEntity[]).map((value) => (
                <option key={value} value={value}>
                  {entityLabels[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Expected interval (seconds)">
            <Input
              id="monitor-interval"
              type="number"
              min={60}
              step={30}
              placeholder="300"
              value={formData.expected_interval_sec}
              onChange={(e) => setFormData({ ...formData, expected_interval_sec: Number(e.target.value) })}
              className="tabular-nums"
            />
          </Field>
        </div>
        <Field label="Target" hint="Cron job name, queue consumer code, PHP class::method, or dispatched event name.">
          <Input
            id="monitor-target"
            type="text"
            required
            placeholder="e.g. acme_erp_export_orders or Acme\ERP\Model\OrderExporter::export"
            value={formData.target_identifier}
            onChange={(e) => setFormData({ ...formData, target_identifier: e.target.value })}
            className="input--mono"
          />
        </Field>
        <div className="form-grid form-grid--2">
          <Field label="Warn after (seconds)">
            <Input
              id="monitor-warn"
              type="number"
              min={60}
              value={formData.warn_after_sec}
              onChange={(e) => setFormData({ ...formData, warn_after_sec: Number(e.target.value) })}
              className="tabular-nums"
            />
          </Field>
          <Field label="Critical after (seconds)">
            <Input
              id="monitor-critical"
              type="number"
              min={60}
              value={formData.critical_after_sec}
              onChange={(e) => setFormData({ ...formData, critical_after_sec: Number(e.target.value) })}
              className="tabular-nums"
            />
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={isTemplateModalOpen}
        size="lg"
        title="Add a monitor from a template"
        onClose={() => setIsTemplateModalOpen(false)}
      >
        {loadingTemplates ? (
          <LoadingRows label="Loading templates…" rows={3} rowHeight="64px" />
        ) : templates.length === 0 ? (
          <p className="quiet-empty">No templates are available.</p>
        ) : (
          <ul className="ssh-rows" aria-label="Templates">
            {templates.map((tpl) => (
              <li key={tpl.id} className="ssh-rows__item">
                <div className="ssh-rows__head">
                  <div className="ssh-rows__title">
                    <span className="ssh-rows__name">{tpl.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCreateFromTemplate(tpl.id)}
                    disabled={instantiatingTemplate === tpl.id}
                    className="button button--secondary button--small"
                  >
                    {instantiatingTemplate === tpl.id ? 'Creating' : 'Use template'}
                  </button>
                </div>
                <p className="ssh-rows__text">{tpl.description}</p>
                <p className="ssh-rows__text">{integrationSummary(tpl, formatSeconds(tpl.expected_interval_sec))}</p>
                {tpl.default_watchers.length > 0 ? (
                  <div className="ssh-actions">
                    <span className="ssh-meta">Signals watched</span>
                    {tpl.default_watchers.map((w, idx) => (
                      <span key={idx} className="badge">
                        {humanize(w.watcher_type)}: {w.name}
                      </span>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Dialog>

      {selectedWatchersMonitor && (
        <IntegrationWatchersDrawer
          isOpen={true}
          onClose={() => setSelectedWatchersMonitor(null)}
          environmentId={environmentId}
          monitor={selectedWatchersMonitor}
          onMonitorUpdated={(updated) => {
            setSelectedWatchersMonitor(updated);
            setMonitors((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
          }}
        />
      )}
    </Section>
  );
};
